import React from 'react'

import {
  Alert as ShadcnAlert,
  AlertDescription,
  AlertTitle,
} from 'src/components/ui/alert'
import {
  CheckCircleFilled,
  CloseCircleFilled,
  CloseOutlined,
  ExclamationCircleFilled,
  InfoCircleFilled,
} from 'src/icons/antd-compat'
import { cn } from 'src/lib/utils'

import type { AlertProps } from './antd-props'

/* -------------------------------------------------------------------------
 * The SHADCN half of the `src/antd/Alert` seam. `./Alert.tsx` is the
 * dispatcher that chooses between this and antd's own Alert; nothing else
 * imports this file, and nothing reaches it until someone flips the design
 * toggle (`src/design-mode`).
 *
 * Ported from the `update-design` reference branch's `src/antd/Alert.tsx`
 * (its Phase 2 slice 11 part B). Divergences from the reference are marked
 * `[development]` below; everything else is the reference verbatim.
 *
 * The EXTERNAL API is antd's own `AlertProps`, so the 259 files that already
 * import `Alert` from `src/antd` switch with no edit at all.
 *
 * Every branch below mirrors antd 5.15.4 `es/alert/Alert.js` rather than
 * approximating it — the type/showIcon defaults, the four-way `isClosable`
 * test (note `closeIcon` being `0` or `''` still counts as closable, which is
 * why it is not a truthiness check), the `mergedCloseIcon` precedence, the
 * `iconMapFilled` glyphs and the DOM order icon → content(message,
 * description) → action → close.
 *
 * THE COLOUR IS THE MEANING. An Alert's four types are semantics, not
 * decoration: an error that stops reading as an error is a defect. So the
 * hues are not re-invented here — the tint, the border and the icon all read
 * `--color-wh-{blue,green,gold,red}-500`, which `src/styles/tailwind.css`
 * bridges to the SAME `--wh-*` custom properties every styled-component in
 * this app already paints its status colours from (`src/styles/palette.ts` →
 * `src/styles/ThemeVars.ts`). Nothing here is a literal, and a themed subtree
 * re-points these utilities exactly as it re-points the CSS beside them.
 *
 * [development] `.ant-alert*` IS NOT RE-EMITTED, and this is the one place
 * this port diverges structurally from the reference. Over there antd is
 * deleted, so those names are ours and re-using them costs nothing. Here antd
 * is installed and the toggle is a RUNTIME flip: any page that rendered an
 * antd Alert before the flip has antd's alert stylesheet on it, UNLAYERED, and
 * `.ant-alert` carries padding, border, border-radius and background — i.e.
 * exactly the properties this seam replaces. That is the `.ant-btn` situation,
 * not the `.ant-space` one. So the parts are keyed by `data-slot` only, and
 * the three live surfaces that reached into antd's names each grew a
 * `[data-slot='…']` selector beside the `.ant-alert-…` one:
 * `src/storybook/components/AlertCollapsible` (which also MEASURES its body
 * with a `querySelector`, so the name is a runtime contract there, not just a
 * stylesheet), `src/new-components/record-lease/LeaseBanner` and
 * `…/tickets/id/information/Form/AlertScheduleTicket`.
 *
 * [development] The close affordance carries `aria-label="close"`. antd's own
 * is a `<button class="ant-alert-close-icon">` whose only content is the
 * `CloseOutlined` glyph — which supplies the name through its `role="img"`
 * `aria-label` — so without the explicit label the button would be nameless
 * whenever a call site passes its own `closeIcon`.
 * ------------------------------------------------------------------------- */

type AlertType = NonNullable<AlertProps['type']>

const VARIANT: Record<
  AlertType,
  'info' | 'success' | 'warning' | 'destructive'
> = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  error: 'destructive',
}

/** antd's `iconMapFilled` (es/alert/Alert.js). */
const ICON: Record<AlertType, React.ComponentType> = {
  success: CheckCircleFilled,
  info: InfoCircleFilled,
  error: CloseCircleFilled,
  warning: ExclamationCircleFilled,
}

const ICON_TONE: Record<AlertType, string> = {
  info: 'text-wh-blue-500',
  success: 'text-wh-green-500',
  warning: 'text-wh-gold-500',
  error: 'text-wh-red-500',
}

/** antd forwards only `aria-` and `data-` attributes from the leftover props. */
const pickAriaData = (props: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(props).filter(
      ([k]) => k.startsWith('aria-') || k.startsWith('data-')
    )
  )

export const Alert = ({
  type,
  message,
  description,
  showIcon,
  icon,
  action,
  closable,
  closeIcon,
  closeText,
  onClose,
  afterClose,
  onMouseEnter,
  onMouseLeave,
  onClick,
  banner,
  role = 'alert',
  className,
  style,
  // antd-only props that must never reach the DOM
  prefixCls: _prefixCls,
  rootClassName: _rootClassName,
  ...rest
}: AlertProps) => {
  const [closed, setClosed] = React.useState(false)

  // antd: an explicit `type` wins; otherwise banner mode defaults to
  // 'warning' and everything else to 'info'.
  const mergedType: AlertType = type ?? (banner ? 'warning' : 'info')

  // antd: banner mode defaults to showing the icon.
  const isShowIcon = banner && showIcon === undefined ? true : showIcon

  // antd's four-way test.
  const isClosable = (() => {
    if (typeof closable === 'object' && closable.closeIcon) return true
    if (closeText) return true
    if (typeof closable === 'boolean') return closable
    return closeIcon !== false && closeIcon !== null && closeIcon !== undefined
  })()

  // antd's precedence: closable.closeIcon, then closeText, then closeIcon,
  // else the default glyph.
  const mergedCloseIcon = (() => {
    if (typeof closable === 'object' && closable.closeIcon)
      return closable.closeIcon
    if (closeText) return closeText
    if (closeIcon !== undefined && closeIcon !== true) return closeIcon
    return <CloseOutlined />
  })()

  const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClose?.(e)
    setClosed(true)
    // antd fires afterClose when its leave motion ends; there is no motion
    // here, so the close is the end of it.
    afterClose?.()
  }

  if (closed) return null

  const IconGlyph = ICON[mergedType]

  return (
    <ShadcnAlert
      variant={VARIANT[mergedType]}
      role={role}
      className={cn(
        banner && 'rounded-none border-x-0 border-t-0',
        // The shadcn Alert is a 2-column grid (icon, content). antd also
        // hangs an action and a close button off the right edge.
        'grid-cols-[auto_1fr_auto_auto] items-start gap-x-3',
        className
      )}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      {...pickAriaData(rest as Record<string, unknown>)}
    >
      {isShowIcon ? (
        <span
          data-slot="alert-icon"
          className={cn(
            'col-start-1 row-span-2 self-start pt-0.5',
            ICON_TONE[mergedType]
          )}
        >
          {icon ?? <IconGlyph />}
        </span>
      ) : null}

      <div
        data-slot="alert-content"
        className={cn(
          'col-start-2 row-span-2 min-w-0',
          !isShowIcon && 'col-start-1'
        )}
      >
        {message ? (
          <AlertTitle className="col-start-1 line-clamp-none">
            {message}
          </AlertTitle>
        ) : null}
        {description ? (
          <AlertDescription className="col-start-1">
            {description}
          </AlertDescription>
        ) : null}
      </div>

      {action ? (
        <div
          data-slot="alert-action"
          className="col-start-3 row-span-2 self-center"
        >
          {action}
        </div>
      ) : null}

      {isClosable ? (
        <button
          type="button"
          data-slot="alert-close"
          aria-label="close"
          onClick={handleClose}
          className="col-start-4 row-span-2 m-0 self-start border-0 bg-transparent p-0 [font-family:inherit] text-muted-foreground hover:text-foreground"
        >
          {mergedCloseIcon}
        </button>
      ) : null}
    </ShadcnAlert>
  )
}
